import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import Modal from "react-bootstrap/Modal";

const documentTypeLabels = {
    PAN: "PAN Card",
    Aadhaar: "Aadhaar Card",
    GST: "GST Certificate",
    RegistrationCertificate: "Registration Certificate",
    IncorporationCertificate: "Incorporation Certificate",
    MOA: "MOA",
    AOA: "AOA",
    Passport: "Passport",
    VoterId: "Voter ID",
};

const CompanyKYCDetails = () => {
    const { companyId } = useParams();
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();

    const [company, setCompany] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [pageLoading, setPageLoading] = useState(true);
    const [btnLoading, setBtnLoading] = useState(false);
    const [remarks, setRemarks] = useState({});
    const [verificationNote, setVerificationNote] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [addDocShow, setAddDocShow] = useState(false);
    const [addDocValues, setAddDocValues] = useState({
        companyDocumentId: "00000000-0000-0000-0000-000000000000",
        companyId: companyId,
        documentType: "",
        imageFile: null,
        remarks: "",
    });
    const [addDocErrors, setAddDocErrors] = useState({});
    const [addDocBtnLoading, setAddDocBtnLoading] = useState(false);
    const [addDocPreview, setAddDocPreview] = useState(null);
    const [savingRemark, setSavingRemark] = useState({});
    const [selectedKYCStatus, setSelectedKYCStatus] =
    useState("Pending");

    const GetData = async () => {
        setPageLoading(true);
        try {
            const [companyRes, docsRes] = await Promise.all([
                apiService.getSingle(`${config.GETCOMPANYBYID}/${companyId}`),
                apiService.getSingle(
                    `${config.GETCOMPANYDOCUMENTSBYCOMPANY}/${companyId}`
                ),
            ]);
            setCompany(companyRes);
            setSelectedKYCStatus(
    companyRes?.isKYC ? "Verified" : "Pending"
);
            setDocuments(docsRes || []);

            // Initialize remarks state from existing doc remarks
            const initialRemarks = {};
            (docsRes || []).forEach((doc) => {
                initialRemarks[doc.companyDocumentId] = doc.remarks || "";
            });
            setRemarks(initialRemarks);
        } catch (error) {
            console.error("Error fetching KYC data:", error);
        } finally {
            setPageLoading(false);
        }
    };

    useEffect(() => {
        if (!authLoading && user) {
            GetData();
        }
    }, [user, authLoading]);

    const handleRemarkChange = (docId, value) => {
        setRemarks((prev) => ({ ...prev, [docId]: value }));
    };

    //   const handleSaveRemark = async (doc) => {
    //     try {
    //       const formData = new FormData();
    //       formData.append("companyDocumentId", doc.companyDocumentId);
    //       formData.append("companyId", doc.companyId);
    //       formData.append("documentType", doc.documentType);
    //       formData.append("remarks", remarks[doc.companyDocumentId] || "");
    //       await apiService.update(config.UPDATECOMPANYDOCUMENT, formData);
    //     } catch (err) {
    //       console.error("Failed to save remark", err);
    //     }
    //   };
    const handleAddDocShow = () => {
        setAddDocValues({
            companyDocumentId: "00000000-0000-0000-0000-000000000000",
            companyId: companyId,
            documentType: "",
            imageFile: null,
            remarks: "",
        });
        setAddDocPreview(null);
        setAddDocErrors({});
        setAddDocShow(true);
    };

    const handleAddDocClose = () => {
        setAddDocShow(false);
        setAddDocPreview(null);
    };

    const handleAddDocFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setAddDocValues((prev) => ({ ...prev, imageFile: file }));
            if (file.type.startsWith("image/")) {
                setAddDocPreview(URL.createObjectURL(file));
            } else {
                setAddDocPreview(null);
            }
        }
    };

    const handleAddDocSubmit = async (e) => {
        e.preventDefault();
        let temp = {};
        temp.documentType = addDocValues.documentType === "" ? false : true;
        temp.imageFile = addDocValues.imageFile === null ? false : true;
        setAddDocErrors(temp);
        if (!Object.values(temp).every((x) => x === true)) return;

        setAddDocBtnLoading(true);
        try {
            const formData = new FormData();
            formData.append("companyDocumentId", addDocValues.companyDocumentId);
            formData.append("companyId", companyId);
            formData.append("documentType", addDocValues.documentType);
            formData.append("remarks", addDocValues.remarks || "");
            formData.append("imageFile", addDocValues.imageFile);
            await apiService.create(config.CREATECOMPANYDOCUMENT, formData);
            await GetData();
            setAddDocShow(false);
        } finally {
            setAddDocBtnLoading(false);
        }
    };

    const handleEditDoc = (doc) => {
        setAddDocValues({
            companyDocumentId: doc.companyDocumentId,
            companyId: doc.companyId,
            documentType: doc.documentType,
            imageFile: null,
            remarks: doc.remarks || "",
            imageName: doc.imageName,
        });
        setAddDocPreview(doc.imageSrc || null);
        setAddDocErrors({});
        setAddDocShow(true);
    };

    const handleDeleteDoc = async (id) => {
        if (window.confirm("Are you sure you want to delete this document?")) {
            try {
                await apiService.remove(config.DELETECOMPANYDOCUMENT, id);
                await GetData();
            } catch (err) {
                console.error("Failed to delete document", err);
            }
        }
    };

    const handleSaveRemark = async (doc) => {
        setSavingRemark((prev) => ({ ...prev, [doc.companyDocumentId]: true }));
        try {
            const formData = new FormData();
            formData.append("companyDocumentId", doc.companyDocumentId);
            formData.append("companyId", doc.companyId);
            formData.append("documentType", doc.documentType);
            formData.append("remarks", remarks[doc.companyDocumentId] || "");
            await apiService.update(config.UPDATECOMPANYDOCUMENT, formData);
        } catch (err) {
            console.error("Failed to save remark", err);
        } finally {
            setSavingRemark((prev) => ({ ...prev, [doc.companyDocumentId]: false }));
        }
    };

    const handleUpdateKYCStatus = async (isKYC) => {
        setBtnLoading(true);
        setSuccessMsg("");
        setErrorMsg("");
        try {
            await apiService.update(
                `${config.UPDATEKYCSTATUS}/${companyId}/${isKYC}`
            );
            setCompany((prev) => ({ ...prev, isKYC }));
            setSuccessMsg(
                isKYC
                    ? "KYC verified successfully."
                    : "KYC rejected. Agent has been notified."
            );
            await GetData();
        } catch (error) {
            setErrorMsg("Failed to update KYC status. Please try again.");
        } finally {
            setBtnLoading(false);
        }
    };

    if (authLoading || pageLoading) {
        return (
            <div id="layout-wrapper">
                <Header User={user} />
                <div className="main-content">
                    <div className="page-content">
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status" />
                            <p className="mt-2 text-muted">Loading KYC details...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div id="layout-wrapper">
            <Header User={user} />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">

                        {/* Back Button */}
                        <div className="mb-3">
                            <button
                                className="btn btn-light"
                                onClick={() => navigate(-1)}
                            >
                                <i className="ri-arrow-left-line me-1" />
                                Back to Clients
                            </button>
                        </div>

                        {/* Company Info Card */}
                     <div className="row mb-4">

    {/* Company Details */}
    <div className="col-lg-8">
        <div className="card h-100">
            <div className="card-body">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <div>
                        <h5 className="mb-1">{company?.companyName}</h5>

                        <div className="d-flex gap-3 flex-wrap">
                            <span className="text-muted">
                                <i className="ri-mail-line me-1" />
                                {company?.email}
                            </span>

                            <span className="text-muted">
                                <i className="ri-phone-line me-1" />
                                {company?.phoneNumber}
                            </span>

                            {company?.entityType && (
                                <span className="badge bg-secondary">
                                    {company.entityType}
                                </span>
                            )}

                            <span className="text-muted">
                                KYC Status:
                            </span>

                            {company?.isKYC ? (
                                <span className="badge bg-success fs-12">
                                    <i className="ri-shield-check-fill me-1" />
                                    Verified
                                </span>
                            ) : (
                                <span className="badge bg-warning text-dark fs-12">
                                    <i className="ri-error-warning-fill me-1" />
                                    Pending
                                </span>
                            )}

                            <span className="text-muted">
                                Plan:
                            </span>

                            <span className="badge bg-primary">
                                {company?.planType || "N/A"}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    {/* KYC Actions */}
   <div className="col-lg-4">
    <div className="card h-100">
        <div className="card-body">

            <label className="form-label fw-semibold">
                KYC Status
            </label>

            <div className="d-flex gap-2 align-items-center">

                <select
                    className="form-select"
                    value={selectedKYCStatus}
                    onChange={(e) => setSelectedKYCStatus(e.target.value)}
                >
                    <option value="Pending">Pending</option>
                    <option value="Verified">Verified</option>
                </select>

                <button
                    className="btn btn-primary"
                    disabled={btnLoading || documents.length === 0}
                    onClick={() =>
                        handleUpdateKYCStatus(
                            selectedKYCStatus === "Verified"
                        )
                    }
                >
                    {btnLoading ? (
                        <span className="spinner-border spinner-border-sm" />
                    ) : (
                        <>
                            <i className="ri-save-line me-1" />
                            Update
                        </>
                    )}
                </button>

            </div>

            {documents.length === 0 && (
                <small className="text-danger mt-2 d-block">
                    <i className="ri-error-warning-line me-1" />
                    Cannot verify KYC — no documents uploaded yet.
                </small>
            )}

            {company?.isKYC && (
                <small className="text-muted mt-2 d-block">
                    <i className="ri-information-line me-1" />
                    KYC is already verified.
                </small>
            )}

        </div>
    </div>
</div>

</div>

                        {/* Success / Error Messages */}
                        {successMsg && (
                            <div className="alert alert-success alert-dismissible">
                                <i className="ri-check-line me-2" />
                                {successMsg}
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setSuccessMsg("")}
                                />
                            </div>
                        )}
                        {errorMsg && (
                            <div className="alert alert-danger alert-dismissible">
                                <i className="ri-error-warning-line me-2" />
                                {errorMsg}
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setErrorMsg("")}
                                />
                            </div>
                        )}

                        {/* Documents Section */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="mb-0">
                                    <i className="ri-file-list-3-line me-2" />
                                    Uploaded Documents ({documents.length})
                                </h5>
                            </div>
                            <div className="card-body">
                                {documents.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i
                                            className="ri-file-upload-line"
                                            style={{ fontSize: "48px", color: "#ccc" }}
                                        />
                                        <p className="text-muted mt-2">
                                            No documents uploaded by this company yet.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="row g-4">
                                        {documents.map((doc) => (
                                            <div
                                                className="col-xl-3 col-lg-4 col-md-6"
                                                key={doc.companyDocumentId}
                                            >
                                                <div className="card border h-100 shadow-sm">

                                                    {/* Preview */}
                                                    <div
                                                        className="d-flex align-items-center justify-content-center bg-light"
                                                        style={{
                                                            height: "180px",
                                                            overflow: "hidden"
                                                        }}
                                                    >
                                                        {doc.imageSrc &&
                                                            (doc.imageSrc.toLowerCase().endsWith(".jpg") ||
                                                                doc.imageSrc.toLowerCase().endsWith(".jpeg") ||
                                                                doc.imageSrc.toLowerCase().endsWith(".png") ||
                                                                doc.imageSrc.toLowerCase().endsWith(".webp")) ? (
                                                            <img
                                                                src={doc.imageSrc}
                                                                alt={doc.documentType}
                                                                style={{
                                                                    width: "100%",
                                                                    height: "100%",
                                                                    objectFit: "cover"
                                                                }}
                                                            />
                                                        ) : (
                                                            <div className="text-center">
                                                                <i
                                                                    className="ri-file-pdf-line"
                                                                    style={{
                                                                        fontSize: "52px",
                                                                        color: "#e74c3c"
                                                                    }}
                                                                />
                                                                <p className="text-muted small mt-1">
                                                                    Document
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="card-body p-3">
                                                        {/* Document Type + Date */}
                                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                                            <span className="badge bg-primary">
                                                                {documentTypeLabels[doc.documentType] || doc.documentType}
                                                            </span>
                                                            <small className="text-muted">
                                                                {new Date(doc.createdDate).toLocaleDateString()}
                                                            </small>
                                                        </div>

                                                        {/* View + Download */}
                                                        {doc.imageSrc && (
                                                            <div className="d-flex gap-2 mb-2 flex-wrap">
                                                                <a
                                                                    href={doc.imageSrc}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="btn btn-sm btn-info flex-fill"
                                                                >
                                                                    <i className="ri-eye-line me-1" />
                                                                    View
                                                                </a>

                                                                <a
                                                                    href={doc.imageSrc}
                                                                    download
                                                                    className="btn btn-sm btn-secondary flex-fill"
                                                                >
                                                                    <i className="ri-download-line me-1" />
                                                                    Download
                                                                </a>
                                                            </div>
                                                        )}

                                                        {/* Edit + Delete */}
                                                        <div className="d-flex gap-2 mb-3">
                                                            <button
                                                                className="btn btn-sm btn-warning flex-fill"
                                                                onClick={() => handleEditDoc(doc)}
                                                            >
                                                                <i className="ri-pencil-line me-1" />
                                                                Edit
                                                            </button>

                                                            <button
                                                                className="btn btn-sm btn-danger flex-fill"
                                                                onClick={() => handleDeleteDoc(doc.companyDocumentId)}
                                                            >
                                                                <i className="ri-delete-bin-line me-1" />
                                                                Delete
                                                            </button>
                                                        </div>

                                                        {/* Admin Remarks */}
                                                        <div>
                                                            <label className="form-label small fw-bold text-muted mb-1">
                                                                Admin Remarks
                                                            </label>

                                                            <textarea
                                                                className="form-control form-control-sm"
                                                                rows={2}
                                                                placeholder="Add remarks for this document..."
                                                                value={remarks[doc.companyDocumentId] || ""}
                                                                onChange={(e) =>
                                                                    handleRemarkChange(doc.companyDocumentId, e.target.value)
                                                                }
                                                            />

                                                            <button
                                                                className="btn btn-sm btn-primary mt-2 w-100"
                                                                disabled={savingRemark[doc.companyDocumentId]}
                                                                onClick={() => handleSaveRemark(doc)}
                                                            >
                                                                {savingRemark[doc.companyDocumentId] ? (
                                                                    <>
                                                                        <span className="spinner-border spinner-border-sm me-1" />
                                                                        Saving...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <i className="ri-save-line me-1" />
                                                                        Update Remarks
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Verification Actions Card */}
                       

                    </div>
                </div>
            </div>
            {/* Add / Edit Document Modal */}
<Modal show={addDocShow} onHide={handleAddDocClose}>
  <Modal.Header closeButton>
    <Modal.Title>
      {addDocValues.companyDocumentId === "00000000-0000-0000-0000-000000000000"
        ? "Add Document"
        : "Edit Document"}
    </Modal.Title>
  </Modal.Header>
  <Modal.Body>
    <form onSubmit={handleAddDocSubmit} autoComplete="off" noValidate>
      <div className="row">
        {/* Document Type */}
        <div className="col-lg-12 mb-3">
          <label className="form-label">
            Document Type <span className="text-danger">*</span>
          </label>
          <select
            value={addDocValues.documentType}
            onChange={(e) =>
              setAddDocValues((prev) => ({
                ...prev,
                documentType: e.target.value,
              }))
            }
            className={
              "form-select" +
              (addDocErrors.documentType === false ? " border-danger" : "")
            }
          >
            <option value="">-- Select Document Type --</option>
            {Object.entries(documentTypeLabels).map(([val, label]) => (
              <option key={val} value={val}>
                {label}
              </option>
            ))}
          </select>
          {addDocErrors.documentType === false && (
            <div className="text-danger small mt-1">
              Please select document type
            </div>
          )}
        </div>

        {/* File Upload */}
        <div className="col-lg-12 mb-3">
          <label className="form-label">
            Upload File{" "}
            {addDocValues.companyDocumentId ===
              "00000000-0000-0000-0000-000000000000" && (
              <span className="text-danger">*</span>
            )}
          </label>
          <input
            type="file"
            onChange={handleAddDocFileChange}
            className={
              "form-control" +
              (addDocErrors.imageFile === false ? " border-danger" : "")
            }
            accept="image/*,.pdf"
          />
          <small className="text-muted">
            Accepted formats: JPG, PNG, PDF
          </small>
          {addDocErrors.imageFile === false && (
            <div className="text-danger small mt-1">
              Please upload a document
            </div>
          )}
          {addDocPreview && (
            <div className="mt-2">
              <img
                src={addDocPreview}
                alt="Preview"
                style={{
                  width: "100%",
                  maxHeight: "200px",
                  objectFit: "contain",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                }}
              />
            </div>
          )}
          {addDocValues.companyDocumentId !==
            "00000000-0000-0000-0000-000000000000" &&
            !addDocValues.imageFile &&
            addDocValues.imageName && (
              <div className="mt-2 text-muted small">
                <i className="ri-file-line me-1" />
                Current file: {addDocValues.imageName}
              </div>
            )}
        </div>

        {/* Remarks */}
        <div className="col-lg-12 mb-3">
          <label className="form-label">Remarks (optional)</label>
          <textarea
            value={addDocValues.remarks || ""}
            onChange={(e) =>
              setAddDocValues((prev) => ({
                ...prev,
                remarks: e.target.value,
              }))
            }
            className="form-control"
            placeholder="Any notes about this document..."
            rows={3}
            maxLength={500}
          />
        </div>
      </div>

      <Modal.Footer>
        <div className="hstack gap-2 justify-content-end">
          {addDocBtnLoading ? (
            <button className="btn btn-primary" disabled>
              Please wait...
            </button>
          ) : (
            <button type="submit" className="btn btn-primary">
              {addDocValues.companyDocumentId ===
              "00000000-0000-0000-0000-000000000000"
                ? "Upload"
                : "Update"}
            </button>
          )}
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleAddDocClose}
          >
            Cancel
          </button>
        </div>
      </Modal.Footer>
    </form>
  </Modal.Body>
</Modal>
        </div>
        
       
    );
};

export default CompanyKYCDetails;